import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Trash2, History, RotateCcw, Layers, Copy, FileText, MoreHorizontal, Pencil, Plus, Search, Sparkles, X, Eye } from 'lucide-react';
import { formBuilderApi } from './api';
import { FORM_TAG_LABELS, FormSummary, FormTag, Option } from './types';
import NewFormDialog from './editor/NewFormDialog';
import DuplicateDialog, { DuplicateSelection } from './editor/DuplicateDialog';
import { primaryButton, secondaryButton, Skeleton } from './ui';
import { TourButton } from './tour/TourButton';
import { ROUTES } from '../../config/links';

export default function FormBuilderListPage() {
  const [forms, setForms] = useState<FormSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  type StatusFilter = 'all' | 'draft' | 'published' | 'inactive';
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status');
  const [status, setStatus] = useState<StatusFilter>(
    initialStatus === 'draft' || initialStatus === 'published' || initialStatus === 'inactive' ? initialStatus : 'all',
  );
  const [sensorFilter, setSensorFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tagFilter, setTagFilter] = useState<FormTag | ''>('');
  const [sensorOptions, setSensorOptions] = useState<Option[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<Option[]>([]);
  const [newFormOpen, setNewFormOpen] = useState(!!(useLocation().state as { openNew?: boolean } | null)?.openNew);
  const [duplicateSource, setDuplicateSource] = useState<FormSummary>();
  const [duplicating, setDuplicating] = useState(false);
  const [menuId, setMenuId] = useState<string>();
  const navigate = useNavigate();

  const load = (queryValue = query, statusValue = status) => {
    setLoading(true);
    formBuilderApi.list({
      q: queryValue.trim() || undefined,
      status: statusValue,
      sensor_type: sensorFilter || undefined,
      category: categoryFilter || undefined,
      tag: tagFilter || undefined,
    })
      .then(setForms)
      .catch((e) => toast.error(e?.response?.data?.detail || e?.message || 'Failed to load forms'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const timer = window.setTimeout(() => load(), query ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [query, status, sensorFilter, categoryFilter, tagFilter]);

  useEffect(() => {
    formBuilderApi.sensorTypeOptions().then(setSensorOptions).catch(() => setSensorOptions([]));
  }, []);

  // Category choices depend on the selected sensor type; the filter resets when it changes.
  useEffect(() => {
    setCategoryFilter('');
    setCategoryOptions([]);
    if (!sensorFilter) return;
    formBuilderApi.categoryOptions(sensorFilter).then(setCategoryOptions).catch(() => setCategoryOptions([]));
  }, [sensorFilter]);

  const sensorLabel = (value?: string | null) => sensorOptions.find((o) => o.value === value)?.label || value;
  const startNewForm = (selection: { sensor_type: string; category: string; tags: FormTag[] }) => {
    setNewFormOpen(false);
    navigate(ROUTES.FORM_BUILDER_NEW, { state: selection });
  };
  const hasFilters = !!(query || sensorFilter || categoryFilter || tagFilter || status !== 'all');
  const clearFilters = () => { setQuery(''); setStatus('all'); setSensorFilter(''); setCategoryFilter(''); setTagFilter(''); };

  const firstPublishedId = forms.find((f) => f.is_active && f.published_version)?.id;

  const openDuplicate = (form: FormSummary) => {
    setDuplicateSource(form);
  };

  const duplicate = async (selection: DuplicateSelection) => {
    if (!duplicateSource) return;
    setDuplicating(true);
    try {
      const created = await formBuilderApi.duplicate(duplicateSource.id, {
        name: selection.name,
        description: duplicateSource.description,
        sensor_type: selection.sensor_type,
        category: selection.category,
        tags: selection.tags,
      });
      toast.success('Form duplicated');
      setDuplicateSource(undefined);
      navigate(ROUTES.formBuilderEdit(created.id));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || e?.message || 'Duplicate failed');
    } finally {
      setDuplicating(false);
    }
  };

  const restore = async (key: string, name: string) => {
    if (!window.confirm(`Restore form "${name}"? It will reappear in All and, if published, in the runtime list.`)) return;
    try { await formBuilderApi.restore(key); toast.success('Form restored'); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || e?.message || 'Restore failed'); }
  };

  const remove = async (key: string) => {
    if (!window.confirm(`Delete form "${key}"? The form is marked inactive and its versions are retained for audit history.`)) return;
    try { await formBuilderApi.remove(key); toast.success('Form deleted'); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || e?.message || 'Delete failed'); }
  };

  return (
    <>
      <div className="min-h-full bg-slate-50 p-3 md:p-8">
      <header data-tour="list-header" className="mb-4 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-600"><Sparkles className="h-3.5 w-3.5" /> Administration</div>
          <h1 className="mt-1 text-xl font-semibold tracking-tight text-slate-900">Form Builder</h1>
          <p className="mt-1 max-w-2xl text-xs leading-5 text-slate-500">Create reusable schema-driven forms, configure behaviour, test them and publish immutable versions.</p>
        </div>
        <div className="flex items-center gap-1.5">
          <TourButton id="list" ready={!loading} />
          <button data-tour="list-new" className={primaryButton} onClick={() => setNewFormOpen(true)}><Plus className="h-4 w-4" /> New form</button>
        </div>
      </header>

      <div data-tour="list-filters" className="mb-3 flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-2.5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <label className="relative min-w-0 flex-1 lg:max-w-md">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by form name or key" className="h-9 w-full rounded-lg border border-transparent bg-slate-50 pl-9 pr-3 text-[12px] text-slate-800 outline-none transition focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100" />
        </label>
        <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Filter forms">
          {(['all', 'draft', 'published', 'inactive'] as const).map((value) => (
            <button key={value} type="button" role="tab" aria-selected={status === value} onClick={() => setStatus(value)}
              className={`h-8 shrink-0 rounded-lg px-3 text-[11px] font-medium capitalize transition ${status === value ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'}`}>
              {value === 'published' ? 'live' : value}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <select aria-label="Filter by sensor type" value={sensorFilter} onChange={(e) => setSensorFilter(e.target.value)} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-slate-600 outline-none focus:border-primary-300">
            <option value="">All sensor types</option>
            {sensorOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select aria-label="Filter by category" value={categoryFilter} disabled={!sensorFilter} title={sensorFilter ? undefined : 'Select a sensor type first'} onChange={(e) => setCategoryFilter(e.target.value)} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-slate-600 outline-none focus:border-primary-300 disabled:opacity-50">
            <option value="">All categories</option>
            {categoryOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select aria-label="Filter by calibration type" value={tagFilter} onChange={(e) => setTagFilter(e.target.value as FormTag | '')} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-slate-600 outline-none focus:border-primary-300">
            <option value="">All calibration types</option>
            {(Object.keys(FORM_TAG_LABELS) as FormTag[]).map((t) => <option key={t} value={t}>{FORM_TAG_LABELS[t]}</option>)}
          </select>
          {hasFilters && <button type="button" onClick={clearFilters} className={`${secondaryButton} h-8 shrink-0`}><X className="h-3.5 w-3.5" /> Clear filters</button>}
        </div>
      </div>

      {loading ? <ListSkeleton /> : forms.length === 0 ? (
        <div className="flex min-h-[360px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white px-6 text-center">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary-50 text-primary-600"><FileText className="h-5 w-5" /></div>
          <h2 className="mt-3 text-sm font-semibold text-slate-800">{hasFilters ? 'No matching forms' : 'No forms yet'}</h2>
          <p className="mt-1 max-w-sm text-[11px] leading-5 text-slate-500">{hasFilters ? 'Try another name, key or filter.' : 'Use New form above to create your first reusable form.'}</p>
          {hasFilters && <button type="button" onClick={clearFilters} className={`${secondaryButton} mt-4`}><X className="h-3.5 w-3.5" /> Clear filters</button>}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {forms.map((f, index) => (
            <article key={f.id} data-tour={index === 0 ? 'list-card' : undefined} className="flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-slate-300 hover:shadow-md">
              <div className="flex items-start gap-2">
                <div className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${!f.is_active ? 'bg-slate-300' : f.status === 'published' ? 'bg-emerald-500' : f.status === 'draft' ? 'bg-amber-400' : 'bg-slate-300'}`} />
                <div className="min-w-0 flex-1">
                  <h2 className="truncate text-[13px] font-semibold text-slate-900" title={f.name}>{f.name}</h2>
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    <StatusBadge status={f.is_active ? f.status : 'inactive'} />
                    {f.is_active && f.published_version ? <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700">Live v{f.published_version}</span> : null}
                    {f.sensor_type && <span className="rounded-md bg-primary-50 px-1.5 py-0.5 text-[9px] font-semibold text-primary-700" title="Sensor type">{sensorLabel(f.sensor_type)}</span>}
                    {f.category && <span className="rounded-md bg-primary-50 px-1.5 py-0.5 text-[9px] font-semibold text-primary-700" title="Category">{f.category}</span>}
                    {(f.tags || []).map((t) => <span key={t} className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-600">{FORM_TAG_LABELS[t] || t}</span>)}
                  </div>
                </div>
                <div className="relative shrink-0" data-tour={index === 0 ? 'list-menu' : undefined}>
                  <button
                    type="button"
                    title="More actions"
                    aria-label={`More actions for ${f.name}`}
                    aria-haspopup="menu"
                    aria-expanded={menuId === f.id}
                    onClick={() => setMenuId(menuId === f.id ? undefined : f.id)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                  ><MoreHorizontal className="h-4 w-4" /></button>
                  {menuId === f.id && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setMenuId(undefined)} />
                      <div role="menu" className="absolute right-0 top-full z-20 mt-1 w-44 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                        {f.is_active && <button role="menuitem" className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-slate-700 transition hover:bg-slate-50" onClick={() => navigate(ROUTES.formBuilderEdit(f.id))}><Pencil className="h-3.5 w-3.5 text-slate-400" /> Edit</button>}
                        <button role="menuitem" className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-slate-700 transition hover:bg-slate-50" onClick={() => navigate(ROUTES.formBuilderAudit(f.id))}><History className="h-3.5 w-3.5 text-slate-400" /> Audit logs</button>
                        {f.is_active && <button role="menuitem" className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-slate-700 transition hover:bg-slate-50" onClick={() => navigate(ROUTES.formBuilderVersions(f.id))}><Layers className="h-3.5 w-3.5 text-slate-400" /> Version history</button>}
                        {!f.is_active && <button role="menuitem" className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-slate-700 transition hover:bg-slate-50" onClick={() => { setMenuId(undefined); restore(f.id, f.name); }}><RotateCcw className="h-3.5 w-3.5 text-slate-400" /> Restore</button>}
                        {f.is_active && <button role="menuitem" className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-slate-700 transition hover:bg-slate-50" onClick={() => { setMenuId(undefined); openDuplicate(f); }}><Copy className="h-3.5 w-3.5 text-slate-400" /> Duplicate</button>}
                        {f.is_active && <button role="menuitem" className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-red-600 transition hover:bg-red-50" onClick={() => { setMenuId(undefined); remove(f.id); }}><Trash2 className="h-3.5 w-3.5" /> Delete</button>}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <p className="mt-3 line-clamp-2 min-h-[2rem] text-[11px] leading-4 text-slate-500" title={f.description || undefined}>{f.description || 'No description'}</p>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-400">
                <span>Updated {new Date(f.updated_at).toLocaleDateString()}</span>
                {f.draft_version && <span>Draft v{f.draft_version}</span>}
                {f.published_version && <span>Published v{f.published_version}</span>}
              </div>
              <div className="mt-auto pt-3">
                  {f.is_active && f.published_version ? <button data-tour={f.id === firstPublishedId ? 'list-runtime' : undefined} className={`${secondaryButton} w-full justify-center`} onClick={() => navigate(ROUTES.formRuntime(f.id))} title="View live form" aria-label={`View live form ${f.name}`}><Eye className="h-3.5 w-3.5" /> View</button> : <div className="h-9" />}
              </div>
            </article>
          ))}
        </div>
      )}
      </div>
      {newFormOpen && <NewFormDialog onClose={() => setNewFormOpen(false)} onContinue={startNewForm} />}
      {duplicateSource && (
        <DuplicateDialog
          initial={{ name: `${duplicateSource.name} copy`, sensor_type: duplicateSource.sensor_type, category: duplicateSource.category, tags: duplicateSource.tags }}
          busy={duplicating}
          onClose={() => setDuplicateSource(undefined)}
          onSubmit={duplicate}
        />
      )}
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls = status === 'published' ? 'bg-emerald-50 text-emerald-700' : status === 'draft' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500';
  return <span className={`rounded-md px-1.5 py-0.5 text-[9px] font-semibold capitalize ${cls}`}>{status}</span>;
}
function ListSkeleton() {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-busy="true" aria-label="Loading forms">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start gap-2">
            <Skeleton className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-16" />
            </div>
            <Skeleton className="h-9 w-9" />
          </div>
          <div className="mt-3 space-y-1.5">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/3" />
          </div>
          <Skeleton className="mt-3 h-2.5 w-44 max-w-full" />
          <Skeleton className="mt-auto h-9 w-full" />
        </div>
      ))}
    </div>
  );
}
